import { can } from "../../../../auth/can.ts";
import { notification } from "../../../../utils/Notifications.tsx";
import { incidentErrorMessage } from "../../../incidents/incidentErrorMessage.ts";
import { isBranchConstrained } from "../../../../auth/sessionAccess.ts";
import React, { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import XLSX from "xlsx-js-style";
import * as moment from "moment-timezone";

import { useAppSettings } from "../../../../hooks/useAppSettings.tsx";
import Title from "../../../Title.tsx";
import Layout from "../../../Layout.tsx";
import { RootState } from "../../../../store/index.ts";
import {
  calcularTiempoTrabajado,
  calcularTurno,
  ReporteRelojChecadorDefaultValues,
} from "../../../../interfaces/formik/IReporteRelojChecador.ts";
import ReporteRelojChecadorForm from "../../../../forms/ReporteRelojChecadorForm.tsx";
import { readBranches } from "../../../../services/branch.service.ts";
import Branch from "../../../../models/Branch.ts";
import ReporteRelojChecadorSchema from "../../../../validation/schemas/ReporteRelojChecadorSchema.ts";
import TableReporteChequeo from "./TableReporteChequeo.tsx";
import {
  findClockRecordsForReport,
  findRecordsForReport,
} from "../../../../services/assistanceCheck.service.ts";
import {
  readClockHoursByBranch,
  readHoursByBranch,
} from "../../../../services/branchSchedule.service.ts";
import ModalContainer from "../../../ModalContainer.tsx";
import { setShow } from "../../../../store/slices/reportClockSlice.ts";
import LogsCheckModal from "./LogsCheckModal.tsx";
import { FormatExcel, SumByDay } from "./DownloadChecks.ts";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";

const Chequeo = () => {
  useAppSettings();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { show } = useSelector((state: RootState) => state.reportClock);
  const { user } = useSelector((state: RootState) => state.auth);
  const [branchs, setBranchs] = useState([]);
  const PAGE_TITLE = "Reporte reloj checador";
  const [pageSubtitle, setPageSubtitle] = useState<string | undefined>();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ReporteRelojChecadorDefaultValues(),
    validationSchema: ReporteRelojChecadorSchema,
    onSubmit: async (values) => {
      setRecords([]);
      setLoading(true);
      try {
        const { branch, partner, start, end } = values;
        if (formik.values.branch !== -99) {
          const branch = branchs.find(
            (branch: Branch) =>
              Number(branch.id) === Number(formik.values.branch)
          ) as any;
          if (branch?.name) {
            setPageSubtitle(branch.name);
          } else if (Number(formik.values.branch) === 0) {
            setPageSubtitle("Todas las sucursales");
          }
        }

        let queryStart = start;
        const queryEnd = moment.utc(end).add(1, "day").format("YYYY-MM-DD");
        const branchNum = Number(branch);
        const clockOnly = !can("attendance.view", user) && can("attendance.check", user);
        if (clockOnly && !(branchNum > 0)) {
          setRecords([]);
          return;
        }
        if (Number.isFinite(branchNum) && branchNum > 0 && branchNum !== -99) {
          try {
            const hours = can("attendance.view", user)
              ? await readHoursByBranch(branchNum)
              : can("attendance.check", user)
                ? await readClockHoursByBranch(branchNum)
                : null;
            if (Boolean(hours?.nightScheduleEnabled)) {
              queryStart = moment
                .utc(start)
                .subtract(1, "day")
                .format("YYYY-MM-DD");
            }
          } catch {
            queryStart = start;
          }
        }

        const data = can("attendance.view", user)
          ? await findRecordsForReport(branch, partner, queryStart, queryEnd)
          : can("attendance.check", user)
            ? await findClockRecordsForReport(branch, partner, queryStart, queryEnd)
            : [];
        setRecords(data);
      } catch (error) {
        setRecords([]);
        notification(
          "Error",
          incidentErrorMessage(error, "No se pudo consultar el reporte de asistencia."),
          "danger",
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchDependencies = async () => {
    const number =
      isBranchConstrained(user) ? Number(user.branchId) : Number(id);
    try {
      const branches = await readBranches();
      const branch = branches.find(
        (branch: Branch) => Number(branch.id) === number
      );
      setPageSubtitle(branch?.name);
      await formik.setFieldValue("branch", branch?.id);
      setBranchs(branches);
    } catch (error) {
      console.log(error);
    } finally {
    }
    setLoading(false);
  };

  const handleDownload = () => {
    const data = FormatExcel(records, formik.values.start, formik.values.end);
    const wb = XLSX.utils.book_new();
    const head = [
      "Fechas consultadas:",
      `${moment(formik.values.start).format("MMMM D [del] YYYY")}`,
      `${moment(formik.values.end).format("MMMM D [del] YYYY")}`,
    ];
    XLSX.utils.sheet_add_aoa(wb, [head], { origin: "A1" });
    const userGenerated = ["Generado por:", `${user?.name} ${user?.lastName}`];
    XLSX.utils.sheet_add_aoa(wb, [userGenerated], { origin: "A2" });

    const headers = [
      "Chilito",
      "Puesto",
      "Turno",
      "Fecha",
      "Entrada",
      "Salida",
      "Horas trabajadas",
      "Sucursal",
    ];
    XLSX.utils.sheet_add_aoa(wb, [headers], { origin: "A3" });
    let rowIndex = 4;
    const backgrounds = [
      "9690f48a",
      "90f4d98a",
      "f4ba90a3",
      "f1f490a3",
      "90a3f4a3",
      "f4c490a3",
    ];
    let ws;
    let partnerIndex = 1;
    let index = 0;

    for (const check of data) {
      const color = backgrounds[partnerIndex % backgrounds.length];

      const name = toCamelCase(
        `${check.checkIn?.person_firstName} ${check.checkIn?.person_lastName} ${check.checkIn?.person_secondLastName}`
      );

      const row = [
        Cell(name, color),
        Cell(check.checkIn?.position_name, color),
        Cell(
          calcularTurno(check.checkIn?.assistanceCheck_checkTime ?? ""),
          color
        ),
        Cell(
          moment
            .utc(check.checkIn?.assistanceCheck_checkDate)
            .format("DD-MM-YYYY"),
          color
        ),
        Cell(check.checkIn?.assistanceCheck_checkTime, color),
        Cell(check.checkOut?.assistanceCheck_checkTime, color),
        Cell(
          String(
            calcularTiempoTrabajado(
              check.checkIn?.assistanceCheck_checkTime!,
              check.checkOut?.assistanceCheck_checkTime
            )
          ),
          color
        ),
        Cell(check.checkIn?.branch_name, color),
      ];

      ws = XLSX.utils.sheet_add_aoa(wb, [row], {
        origin: `A${rowIndex}`,
      });

      const { isLast, totalHours, title } = SumByDay(index, data);
      if (isLast) {
        rowIndex += 1;
        const row = [
          Cell("", color),
          Cell("", color),
          Cell("", color),
          Cell("", color),
          Cell(title, color),
          Cell("", color),
          Cell(String(totalHours), color),
          Cell("", color),
        ];
        ws = XLSX.utils.sheet_add_aoa(wb, [row], {
          origin: `A${rowIndex}`,
        });
        partnerIndex += 1;
      }
      rowIndex += 1;
      index += 1;
    }
    ws["!cols"] = [
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Registros");
    XLSX["!cols"] = [{ wch: 30 }];

    // STEP 4: Write Excel file to browser
    XLSX.writeFile(
      wb,
      `Chequeos - ${moment(formik.values.start).format(
        "DD-MM-YYYY"
      )} - ${moment(formik.values.end).format("DD-MM-YYYY")}.xlsx`
    );
  };
  useEffect(() => {
    if (user) {
      fetchDependencies();
    }
  }, [user]);

  useEffect(() => {
    formik.handleSubmit();
  }, [branchs, id]);

  return (
    <React.Fragment>
      <Layout loading={loading}>
        <Title
          baseTitle="Sucursales"
          basePath="/sucursales"
          activeTitle="Chequeos"
          title={PAGE_TITLE}
          subtitle={pageSubtitle}
        />

        <div
          className="card border-0 mx-2 mx-md-3 mt-2 mt-md-3 mb-3 mb-md-4 shadow-sm overflow-hidden reporte-reloj-form-card"
          style={{ borderRadius: "16px" }}
        >
          <div
            className="w-100 reporte-reloj-form-card-inner"
            style={{
              padding: "clamp(12px, 2.5vw, 20px)",
              boxSizing: "border-box",
            }}
          >
            <ReporteRelojChecadorForm
              availableDownload={true}
              formik={formik}
              branches={branchs}
              disabled={isBranchConstrained(user)}
              handleDownload={() => handleDownload()}
            />
          </div>
        </div>

        <div
          className="card border-0 mx-2 mx-md-3 mb-2 mb-md-3 shadow-sm overflow-hidden p-0 reporte-reloj-table-card"
          style={{ borderRadius: "16px" }}
        >
          <TableReporteChequeo
            records={records}
            loading={loading}
            start={formik.values.start}
            end={formik.values.end}
          />
        </div>
      </Layout>
      <ModalContainer
        open={show}
        title="Registros del reloj checador"
        children={<LogsCheckModal />}
        closeFn={() => dispatch(setShow(false))}
      />
    </React.Fragment>
  );
};

export default Chequeo;

const Cell = (text: string = "", color: string) => {
  return {
    v: text,
    t: "s",
    s: { fill: { fgColor: { rgb: color } } },
  };
};
